// Pure helpers for the extraction flush (persist-bid-tabulations). No DB here —
// these turn the consolidated extraction matrix into the `saveBidTab` row shape
// and suggest vendor normalizations the caller (CLI / API) resolves with a human.

import { toAwardKindOfGroup } from './award';
import { normalizeVendorName } from './normalization/vendor-normalize';

// Re-exported so callers/tests keep a single import surface for persist helpers.
export {
  itemIdentityKey,
  normalizeItemDescription,
} from './normalization/item-normalize';

/** The bid-tab schedule a group belongs to (EDG-755). Mirrors the extraction
 *  model's `BidGroupSchema.type` and the `bid_group_type` pg enum. The two
 *  alternate kinds (EDG-1583) carry the sign a plain `alternate` never did — a
 *  custom bid tab publishes them so the awarded selection can add or deduct. */
export type BidGroupType =
  | 'base'
  | 'supplemental'
  | 'alternate'
  | 'allowance'
  | 'additive_alternate'
  | 'deductive_alternate';

/** The invalid-bid vocabulary (EDG-1588) — a NOT-awardable status a bid can
 *  carry. Mirrors the engine's `BidStatus` MINUS `'valid'`: the matrix only ever
 *  records a status when it is invalid. Kept local so domains needn't depend on
 *  the bid-tab-extract types (same rationale as the matrix mirror below). */
export type InvalidBidStatus =
  | 'irregular'
  | 'non_responsive'
  | 'withdrawn'
  | 'rejected'
  | 'no_bid';

/** The consolidated extraction matrix, structurally (the staging `result`
 *  jsonb / consolidated.json). Loose on purpose so domains needn't depend on
 *  the bid-tab-extract types. */
/** One bid COLUMN — the matrix's vendor axis. `vendorColumnId` (`v0`, `v1`, …) is
 *  the stable positional identity every bids/total map keys on; `name` is the
 *  printed label. Mirrors the engine's `Bidder`; kept local so domains needn't
 *  depend on the bid-tab-extract types. */
export interface MatrixBidder {
  vendorColumnId: string;
  name: string;
  /** The resolved platform vendor this column was linked to during normalization
   *  (`staging.vendors[].vendorId`). Carried through so publish binds the bid to
   *  the vendor the reviewer/normalize picked, instead of re-resolving by the
   *  printed name (which mis-binds a renamed or hand-picked column). Null until
   *  resolved. */
  vendorId?: string | null;
  /** The bid's AWARD STATUS when INVALID (withdrawn / rejected / non_responsive /
   *  irregular / no_bid). Absent for a normal bid. Carried onto the staging vendor
   *  so the UI badges it and the ranking excludes it. */
  status?: InvalidBidStatus;
  /** The printed reason, verbatim. Only when `status` set. */
  statusReason?: string | null;
}

/** The matrix's real columns: a blank-named one is dropped, and an entry that is
 *  a bare printed name is read as the column it stands for. An extraction older
 *  than column ids stored `bidders` that way, and reading `.name` off a string
 *  threw where publish only meant to skip a nameless column (EDG-1629). */
export function matrixBidders(matrix: ConsolidatedMatrix): MatrixBidder[] {
  const bidders = (matrix.bidders ?? []) as (MatrixBidder | string)[];
  return bidders
    .map((b) => (typeof b === 'string' ? ({ name: b } as MatrixBidder) : b))
    .filter((b) => (b.name ?? '').trim().length > 0);
}

export interface ConsolidatedMatrix {
  /** The bid columns in position order — the ONE authority mapping a
   *  `vendorColumnId` back to a printed name. */
  bidders?: MatrixBidder[];
  /** The `vendorColumnId`s that are the owner/engineer reference column(s), not
   *  real bids (EDG-1231). Those columns are diverted to the cost-estimate fields
   *  (proposal + per-item) and kept OUT of the vendor set / cells. */
  estimates?: string[] | null;
  bidGroups?: Array<{
    /** The schedule this group is (BASE BID, ADDITIVE ALTERNATE NO. 1, …). The
     *  consolidated grid sets it (`ConsolidatedGroup.type`); absent/unknown → base. */
    type?: string | null;
    name?: string | null;
    /** EDG-1583: whether this alternate is AWARDED — the reviewer's decision,
     *  carried from `staging`. The base bid ignores it; an awarded additive adds
     *  and an awarded deductive subtracts in the comparison total. */
    awarded?: boolean;
    /** Items live under SCHEDULES in the first-class tree (group → schedules[]
     *  → items). A group may instead carry a flat `items` (legacy/single). */
    schedules?: Array<{
      name?: string | null;
      items?: ConsolidatedMatrixItem[];
      /** The schedule's printed total keyed by `vendorColumnId` — the reconcile
       *  oracle (Σext == schedule). Read off the page, never derived from items. */
      total?: Record<string, number> | null;
    }>;
    items?: ConsolidatedMatrixItem[];
    /** The bid group's printed total keyed by `vendorColumnId` — the reconcile
     *  oracle for the group rung (Σschedules == group). */
    totals?: Record<string, number> | null;
  }>;
  /** The proposal header the CLASSIFY stage read off the page (owner, letting
   *  date, contract/project numbers, cost estimate, …). Carried verbatim into
   *  staging so the Details form shows it. Loose — the UI narrows what it uses. */
  metadata?: Record<string, unknown> | null;
}

/** One line item in a consolidated matrix (under a group or a schedule). */
export interface ConsolidatedMatrixItem {
  /** Stable synthetic ROW identity (item axis) carried from the engine's
   *  consolidated. Optional only because legacy stored matrices predate it. */
  itemLineId?: string;
  /** The line number as PRINTED on the page (the engine's FILL stage reads it;
   *  a reviewer can retype it). Null when the document printed none. */
  lineNumber?: number | null;
  itemNumber?: string | number | null;
  description?: string | null;
  quantity?: number | null;
  unit?: string | null;
  /** Per-vendor cells keyed by `vendorColumnId` (never the name). */
  bids?: Record<
    string,
    { unitPrice?: number | null; extendedPrice?: number | null } | null
  >;
  /** → item_categories.id, when already resolved on staging. */
  categoryId?: string | null;
  /** Collection catalog codes this line belongs to, when already resolved on
   *  staging (e.g. ['asphalt_tons']). */
  collectionCodes?: string[];
  /** Collection memberships WITH their resolved conversion factor, when already
   *  resolved on staging — publish uses the stored factor instead of re-deriving
   *  it. Superset of {@link collectionCodes}. */
  collections?: Array<{ code: string; factor: number; sourceUom: string }>;
  /** → units.id (canonical), when already resolved on staging. */
  unitId?: string | null;
  /** Lump-sum flag, when already resolved on staging. */
  isTrueLumpSum?: boolean | null;
  /** Liquid AC adjustment pattern (P1–P4), when already resolved on staging. */
  liquidAcPattern?: string | null;
}

/** The `saveBidTab`-shaped rows the materializer consumes. */
export interface MatrixRows {
  bidders: string[];
  /** The INVALID bidders (EDG-1588) keyed by name → their status + printed
   *  reason. A name absent here is a normal ('valid') bid. Kept as a side-map so
   *  every `bidders`-as-names reader is untouched; publish reads it to stamp the
   *  bid's status and exclude it from the ranking. */
  bidderStatuses?: Record<
    string,
    { status: InvalidBidStatus; reason: string | null }
  >;
  /** The resolved platform vendor id per bidder NAME, from the staging vendor's
   *  `vendorId` (set during normalization / a reviewer's pick). A side-map so
   *  every `bidders`-as-names reader is untouched; publish reads it to bind the
   *  bid to the linked vendor instead of re-resolving by name. A name absent here
   *  (or null) means "resolve by name" (the legacy behavior). */
  bidderVendorIds?: Record<string, string | null>;
  items: Array<{
    itemNumber: string;
    /** The line number as printed, or null when the document printed none —
     *  publish falls back to the line's position, exactly as the wizard's Line
     *  column and the preview already do. NOT unique within a proposal: a
     *  tabulation whose alternates restart at 1 prints the same number twice,
     *  which is why the uniqueness the DB enforces is on the item number. */
    lineNumber: number | null;
    description: string;
    unit: string | null;
    quantity: string;
    /** The owner/engineer per-line estimate (the diverted estimate column's
     *  extended price), or null. → proposalItem.costEstimate. */
    costEstimate: string | null;
    /** The bid-tab schedule this line belongs to (EDG-755) → proposalItem
     *  bid_group_type/name. A group whose type is absent/unknown coerces to
     *  base with a null name (see toAwardKind). */
    bidGroupType: BidGroupType;
    bidGroupName: string | null;
    /** EDG-1583: whether this line's alternate group is AWARDED → proposalItem
     *  awarded. Always false on the base bid. */
    awarded: boolean;
    /** → item_categories.id, when already resolved on staging. */
    categoryId?: string | null;
    /** Collection catalog codes this line belongs to, when already resolved
     *  on staging. */
    collectionCodes?: string[];
    /** Collection memberships WITH their resolved conversion factor. */
    collections?: Array<{ code: string; factor: number; sourceUom: string }>;
    /** → units.id (canonical), when already resolved on staging. */
    unitId?: string | null;
    /** Lump-sum flag, when already resolved on staging. */
    isTrueLumpSum?: boolean | null;
    /** Liquid AC adjustment pattern (P1–P4), when already resolved on staging. */
    liquidAcPattern?: string | null;
  }>;
  cells: Array<{
    vendorName: string;
    itemNumber: string;
    unitPrice: string;
    extendedPrice: string | null;
  }>;
  /** The document engineer's-estimate total (Σ the estimate column's per-line
   *  extensions), or null when no estimate column was flagged. → proposal.costEstimate. */
  costEstimate: string | null;
}

const num = (n: number | null | undefined): string | null =>
  n == null || !Number.isFinite(n) ? null : String(n);

/**
 * Flatten the consolidated matrix into items + cells. Item numbers are required
 * downstream and unique per row, so a blank/duplicate code is replaced with a
 * synthesized `L<line>` key (lump-sum rows often have none). Numeric values
 * become numeric strings; a cell with neither unit nor extended price is
 * dropped (an empty column the vendor didn't bid).
 */
export function consolidatedMatrixToRows(
  matrix: ConsolidatedMatrix,
): MatrixRows {
  // The flagged estimate column (EDG-1231) is diverted, not bid: drop it from
  // the vendor set so it never becomes a vendor/cell, and route its values to
  // the cost-estimate fields instead. The estimate lives IN `bids` keyed by its
  // `vendorColumnId` (so it stays a real bid in reconcile/review/ranking); the
  // flag is that column id — a strong key, no name matching to slip on.
  const allBidders = matrixBidders(matrix);
  const estimateColumnIds = new Set(matrix.estimates ?? []);
  // EDG-1231 diverts a SINGLE estimate column; take the first flagged one.
  const estimateColumnId =
    allBidders.find((b) => estimateColumnIds.has(b.vendorColumnId))
      ?.vendorColumnId ?? null;
  const bidders = allBidders.filter(
    (b) => b.vendorColumnId !== estimateColumnId,
  );
  const items: MatrixRows['items'] = [];
  const cells: MatrixRows['cells'] = [];
  const usedKeys = new Set<string>();
  let line = 0;
  let estimateTotal = 0;
  let sawEstimate = false;

  for (const group of matrix.bidGroups ?? []) {
    // The award kind carries the sign the DB enum now stores verbatim
    // (`base` / `additive_alternate` / `deductive_alternate`), so a published
    // proposal knows which alternates add and which deduct. `awarded` rides
    // along for the comparison-total math + the results badge.
    // Read the stored type FIRST, then fall back to the printed NAME: a group that
    // came straight from the tabulation assemble carries the ENGINE KIND
    // (`bid_tabulation`), which names no award kind and would coerce a DEDUCTIVE
    // alternate to `base` — flattening it into the base bid, so it could never be
    // awarded and its lines ADDED instead of deducting. That is how newburg's five
    // deductive alternates published as 12 undifferentiated base items.
    const bidGroupType = toAwardKindOfGroup(group.type, group.name);
    const awarded = bidGroupType !== 'base' && group.awarded === true;
    // The printed schedule label, used as the within-proposal grouping key.
    // The base group carries no label (it's the default schedule).
    const bidGroupName =
      bidGroupType === 'base' ? null : group.name?.trim() || null;
    // A group's items live under its SCHEDULES (group → schedules[] → items),
    // the first-class tree shape. Fall back to a flat `group.items` for the
    // legacy/single-schedule shape. Without the schedule walk, every multi-
    // schedule tabulation persists ZERO items.
    const groupItems =
      group.schedules && group.schedules.length > 0
        ? group.schedules.flatMap((s) => s.items ?? [])
        : (group.items ?? []);
    for (const item of groupItems) {
      line += 1;
      const raw = item.itemNumber == null ? '' : String(item.itemNumber).trim();
      let itemNumber = raw || `L${line}`;
      while (usedKeys.has(itemNumber)) {
        itemNumber = `${itemNumber}-${line}`;
      }
      usedKeys.add(itemNumber);

      // The estimate column's value for this row → the per-line cost estimate.
      const estBid = estimateColumnId ? item.bids?.[estimateColumnId] : null;
      const estExt = estBid ? num(estBid.extendedPrice) : null;
      if (estExt != null) {
        sawEstimate = true;
        estimateTotal += Number(estExt);
      }

      items.push({
        itemNumber,
        lineNumber: item.lineNumber ?? null,
        description: item.description?.trim() ?? '',
        unit: item.unit?.trim() || null,
        quantity: num(item.quantity) ?? '1',
        costEstimate: estExt,
        bidGroupType,
        bidGroupName,
        awarded,
        categoryId: item.categoryId ?? null,
        collectionCodes: item.collectionCodes,
        collections: item.collections,
        unitId: item.unitId ?? null,
        isTrueLumpSum: item.isTrueLumpSum ?? null,
        liquidAcPattern: item.liquidAcPattern ?? null,
      });

      for (const vendor of bidders) {
        const bid = item.bids?.[vendor.vendorColumnId];
        if (!bid) {
          continue;
        }
        const unit = num(bid.unitPrice);
        const ext = num(bid.extendedPrice);
        if (unit == null && ext == null) {
          continue;
        }
        // The cell carries the printed NAME — the label the DB stores as
        // `bids.proposalVendorName` and the vendor-record match keys on.
        // TODO(vendor-name-at-site): read the column NAME here for the DB write.
        cells.push({
          vendorName: vendor.name,
          itemNumber,
          unitPrice: unit ?? '0',
          extendedPrice: ext,
        });
      }
    }
  }

  return {
    // The materializer + DB vendor records key by NAME (the label the human sees
    // and the vendor-match resolves), so the rows expose the column names here.
    // TODO(vendor-name-at-site): the row shape is name-keyed at the DB boundary.
    bidders: bidders.map((b) => b.name),
    bidderStatuses: invalidBidderStatuses(bidders),
    bidderVendorIds: resolvedBidderVendorIds(bidders),
    items,
    cells,
    costEstimate: sawEstimate ? String(estimateTotal) : null,
  };
}

/** The invalid bidders keyed by name (EDG-1588). Estimate and blank columns are
 *  already filtered out of `bidders`, so this only names real bid columns that
 *  carry a not-'valid' status; `undefined` when every bid is valid. */
function invalidBidderStatuses(
  bidders: MatrixBidder[],
): MatrixRows['bidderStatuses'] {
  const out: NonNullable<MatrixRows['bidderStatuses']> = {};
  for (const b of bidders) {
    if (b.status) {
      out[b.name] = { status: b.status, reason: b.statusReason ?? null };
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** The resolved `vendorId` per bidder name, from the staging vendor. `undefined`
 *  when no column carries a resolved id (nothing to honor → resolve by name). */
function resolvedBidderVendorIds(
  bidders: MatrixBidder[],
): MatrixRows['bidderVendorIds'] {
  const out: NonNullable<MatrixRows['bidderVendorIds']> = {};
  for (const b of bidders) {
    if (b.vendorId) {
      out[b.name] = b.vendorId;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * A resolved vendor column (the human's staged choice, materialized on push):
 *  - `vendorId` only        → reuse that vendor
 *  - `vendorId` + `renameTo` → reuse it, then rename it (custom-origin only)
 *  - `newName` (no id)      → create a vendor with this exact name
 *  - neither                → auto-create from the bidder `name`
 */
export interface PersistVendor {
  name: string;
  vendorId?: string;
  /** Create a new vendor under this name (when no `vendorId`). */
  newName?: string;
  /** Rename the reused vendor to this (with `vendorId`; custom-origin only). */
  renameTo?: string;
  /** The bid's AWARD STATUS when INVALID (EDG-1588). Absent = valid. */
  status?: InvalidBidStatus;
  /** The printed reason the bid is not valid, verbatim. Only when `status` set. */
  statusReason?: string | null;
}

/** The fully-resolved input to `persistExtraction` — matrix rows + the human's
 *  state / owner / vendor decisions, ready to write. */
export interface PersistExtractionInput {
  ownerName: string;
  stateId?: string | null;
  contractId?: string | null;
  description?: string | null;
  lettingDate?: string | null;
  items: MatrixRows['items'];
  vendors: PersistVendor[];
  cells: MatrixRows['cells'];
  /** Document engineer's-estimate total (EDG-1231) → proposal.costEstimate. */
  costEstimate?: string | null;
  fileId?: string | null;
  uploadSessionId?: string | null;
  /** When set, the source staging session is stamped approved + linked. */
  extractionSessionId?: string | null;
}

// The vendor-identity normalizer lives in its own module (the gate for
// EDG-1087); imported for local use and re-exported for existing callers.
export { normalizeVendorName };

export interface VendorCandidate {
  id: string;
  ownerVendorName: string;
}

export interface VendorMatch {
  /** The extracted bidder name as printed on the bid tab. */
  bidder: string;
  /** The existing vendor whose normalized name matches, or null (pick/create). */
  suggestion: { vendorId: string; name: string } | null;
}

/**
 * For each extracted bidder, suggest the existing owner vendor whose normalized
 * name matches exactly; `null` when there's no match (the caller prompts for a
 * manual pick or a new vendor). The first existing vendor wins a normalized-key
 * collision, keeping the suggestion stable.
 */
export function resolveVendorMatches(
  bidders: string[],
  existingVendors: VendorCandidate[],
): VendorMatch[] {
  const byNorm = new Map<string, VendorCandidate>();
  for (const v of existingVendors) {
    const key = normalizeVendorName(v.ownerVendorName);
    if (key && !byNorm.has(key)) {
      byNorm.set(key, v);
    }
  }
  return bidders.map((bidder) => {
    const match = byNorm.get(normalizeVendorName(bidder));
    return {
      bidder,
      suggestion: match
        ? { vendorId: match.id, name: match.ownerVendorName }
        : null,
    };
  });
}
